import React, { useState, useEffect } from "react"
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet"
import { Icon } from "../ui/icon"
import LeadLookalikes from "./lead_lookalikes"

declare const window: Window & { i18n: { t: (key: string) => string } }

const t = (key: string) => window.i18n.t(key)

interface OpenLookalikesSheetEvent extends CustomEvent {
  detail: {
    leadIds: number[]
    filters?: Record<string, any>
  }
}

interface LeadLookalikesSheetProps {
  buttonLabel: string
  leadIds: number[]
  searchFilters?: Record<string, any>
  user: any
  leadsLists: { id: number; name: string }[]
  numberOfCreditsLeft: number
  similarityThresholds: {
    medium: number
    high: number
  }
  maxLookalikesLimit: number
  isPremium: boolean
  upgradeUrl: string
  upgradeImageUrl: string
}

const LeadLookalikesSheet: React.FC<LeadLookalikesSheetProps> = ({
  buttonLabel,
  leadIds: initialLeadIds,
  searchFilters: initialSearchFilters,
  user,
  leadsLists,
  numberOfCreditsLeft,
  similarityThresholds,
  maxLookalikesLimit,
  isPremium,
  upgradeUrl,
  upgradeImageUrl,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [leadIds, setLeadIds] = useState<number[]>(initialLeadIds)
  const [searchFilters, setSearchFilters] = useState<Record<string, any>>(
    initialSearchFilters || {},
  )

  // Update URL when Sheet opens/closes
  const updateUrl = (open: boolean, ids: number[]) => {
    const urlParams = new URLSearchParams(window.location.search)

    // Remove old params first
    urlParams.delete("lookalikes")
    // Remove all lead_ids[] params
    const keysToDelete = Array.from(urlParams.keys()).filter(
      (k) => k === "lookalikes_lead_ids[]",
    )
    keysToDelete.forEach((k) => urlParams.delete(k))

    if (open) {
      urlParams.set("lookalikes", "1")
      ids.forEach((id) => urlParams.append("lookalikes_lead_ids[]", String(id)))
    }

    const newUrl =
      window.location.pathname +
      (urlParams.toString() ? `?${urlParams.toString()}` : "")
    window.history.replaceState({}, "", newUrl)
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    updateUrl(open, leadIds)
  }

  const openWithLeads = (ids: number[], filters: Record<string, any> = {}) => {
    setLeadIds(ids)
    setSearchFilters(filters)
    setIsOpen(true)
    updateUrl(true, ids)
  }

  // Check URL params on mount for lookalikes=1
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)

    if (urlParams.get("lookalikes") === "1") {
      const leadIdsFromUrl = urlParams.getAll("lookalikes_lead_ids[]")

      const ids =
        leadIdsFromUrl.length > 0
          ? leadIdsFromUrl.map((id) => parseInt(id, 10))
          : initialLeadIds

      setLeadIds(ids)
      setIsOpen(true)
    }
  }, [])

  // Listen for custom event from Stimulus controller (table action button)
  useEffect(() => {
    const handleOpenSheet = (event: OpenLookalikesSheetEvent) => {
      openWithLeads(event.detail.leadIds || [], event.detail.filters || {})
    }

    document.addEventListener(
      "open-lookalikes-sheet",
      handleOpenSheet as EventListener,
    )
    return () => {
      document.removeEventListener(
        "open-lookalikes-sheet",
        handleOpenSheet as EventListener,
      )
    }
  }, [])

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <button className="h-button h-button--sm h-button--ai" type="button">
          <Icon icon="sparkles" />
          {buttonLabel}
        </button>
      </SheetTrigger>
      <SheetContent size="lg" className="p-0">
        {isPremium ? (
          <LeadLookalikes
            leadIds={leadIds}
            searchFilters={searchFilters}
            user={user}
            leadsLists={leadsLists}
            numberOfCreditsLeft={numberOfCreditsLeft}
            similarityThresholds={similarityThresholds}
            maxLookalikesLimit={maxLookalikesLimit}
            onClose={() => setIsOpen(false)}
          />
        ) : (
          <div className="empty-state empty-state--sidepanel gap-4">
            <div className="empty-state__img empty-state__img--full">
              <img src={upgradeImageUrl} alt="" />
            </div>
            <div className="empty-state__content">
              <h3 className="empty-state__title">
                {t("js.lead_lookalikes.upgrade_title")}
              </h3>
              <div
                className="empty-state__description"
                dangerouslySetInnerHTML={{
                  __html: t("js.lead_lookalikes.upgrade_description"),
                }}
              />
              <div className="empty-state__actions">
                <a href={upgradeUrl} className="h-button h-button--primary">
                  {t("js.lead_lookalikes.upgrade_button")}
                </a>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default LeadLookalikesSheet
